import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useProducts, clearProductsCache } from '../hooks/useProducts';
import ProductCard from '../components/ProductCard';
import { SearchBar } from '../components/SearchBar';
import { supabase } from '../services/supabase';

export const Products: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const categoryIdFromUrl = searchParams.get('category');
    const brandFromUrl = searchParams.get('brand');
    const searchFromUrl = searchParams.get('search');

    const containerRef = useRef<HTMLDivElement>(null);

    const [showFilters, setShowFilters] = useState(() =>
        sessionStorage.getItem('products_showFilters') === 'true'
    );
    const [searchQuery, setSearchQuery] = useState(() => {
        return searchFromUrl || sessionStorage.getItem('products_search') || '';
    });
    const [minPrice, setMinPrice] = useState(() =>
        sessionStorage.getItem('products_minPrice') || ''
    );
    const [maxPrice, setMaxPrice] = useState(() =>
        sessionStorage.getItem('products_maxPrice') || ''
    );

    const [categoryName, setCategoryName] = useState<string>('');
    const [displayQuery, setDisplayQuery] = useState<string>('');

    useEffect(() => {
        if (categoryIdFromUrl) {
            supabase
                .from('categories')
                .select('name')
                .eq('id', parseInt(categoryIdFromUrl))
                .single()
                .then(({ data }) => {
                    if (data) {
                        setCategoryName(data.name);
                        setDisplayQuery(data.name);
                    }
                });
        } else if (brandFromUrl) {
            setDisplayQuery(brandFromUrl);
        } else if (searchFromUrl) {
            setDisplayQuery(searchFromUrl);
        } else {
            setDisplayQuery('');
            setCategoryName('');
        }
    }, [categoryIdFromUrl, brandFromUrl, searchFromUrl]);

    const effectiveCategoryId = categoryIdFromUrl;
    const effectiveSearch = brandFromUrl || searchFromUrl || searchQuery;

    const {
        products,
        total,
        loading,
        loadingMore,
        hasMore,
        loadMore,
        isRevalidating,
        isOnline,
    } = useProducts({
        categoryId: effectiveCategoryId,
        search: effectiveSearch,
        minPrice: minPrice,
        maxPrice: maxPrice,
    });

    useEffect(() => {
        sessionStorage.setItem('products_showFilters', String(showFilters));
        sessionStorage.setItem('products_search', searchQuery);
        sessionStorage.setItem('products_minPrice', minPrice);
        sessionStorage.setItem('products_maxPrice', maxPrice);
    }, [showFilters, searchQuery, minPrice, maxPrice]);

    // Infinite scroll
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);

    useEffect(() => {
        if (observerRef.current) observerRef.current.disconnect();
        observerRef.current = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && hasMore && !loading && !loadingMore) loadMore();
            },
            { rootMargin: '400px', threshold: 0 }
        );
        if (sentinelRef.current) observerRef.current.observe(sentinelRef.current);
        return () => observerRef.current?.disconnect();
    }, [hasMore, loading, loadingMore, loadMore]);

    // ✅ ИСПРАВЛЕНО: сбрасываем кеш ДО изменения URL,
    // чтобы useProducts не показывал старые данные при новом optionsKey
    const resetFilters = useCallback(() => {
        // Очищаем весь кеш продуктов — useProducts сам перезагрузит данные
        clearProductsCache();

        setSearchQuery('');
        setMinPrice('');
        setMaxPrice('');
        setShowFilters(false);
        setDisplayQuery('');
        setCategoryName('');

        // Очищаем URL — useProducts отреагирует на новый optionsKey
        setSearchParams({});

        sessionStorage.removeItem('products_search');
        sessionStorage.removeItem('products_minPrice');
        sessionStorage.removeItem('products_maxPrice');
        sessionStorage.removeItem('products_showFilters');

        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [setSearchParams]);

    const handleClearSearch = () => {
        resetFilters();
    };

    const hasActiveFilters = searchQuery !== '' || minPrice !== '' || maxPrice !== '' || categoryIdFromUrl || brandFromUrl;

    const [showScrollButton, setShowScrollButton] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setShowScrollButton(window.scrollY > 300);
        };
        window.addEventListener('scroll', handleScroll);
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const getPageTitle = () => {
        if (categoryIdFromUrl && categoryName) return categoryName;
        if (categoryIdFromUrl) return 'Категория';
        if (brandFromUrl) return `Товары бренда ${brandFromUrl}`;
        if (searchFromUrl) return 'Результаты поиска';
        return 'Все товары';
    };

    const getSubtitle = () => {
        if (total > 0 && (searchFromUrl || brandFromUrl || categoryIdFromUrl)) {
            return `Найдено ${total} товаров`;
        }
        return null;
    };

    return (
        <div className="products-container" ref={containerRef}>
            {!isOnline && (
                <div className="offline-banner">
                    📵 Нет подключения — показаны сохранённые товары
                </div>
            )}
            {isRevalidating && isOnline && (
                <div className="revalidating-indicator">
                    <div className="spinner-tiny" /> Обновление...
                </div>
            )}

            <div className="products-header">
                <div>
                    <h1 className="page-title">{getPageTitle()}</h1>
                    {getSubtitle() && (
                        <p className="page-subtitle">{getSubtitle()}</p>
                    )}
                </div>
                <button
                    onClick={() => setShowFilters(v => !v)}
                    className={`filter-toggle-btn${showFilters ? ' active' : ''}`}
                >
                    {showFilters ? '✕ Закрыть' : '⚡ Фильтры'}
                    {hasActiveFilters && !showFilters && <span className="filter-badge" />}
                </button>
            </div>

            <SearchBar
                placeholder="Поиск товаров..."
                value={displayQuery || searchQuery}
                onChange={setSearchQuery}
                onCancel={handleClearSearch}
                showCancel={!!(categoryIdFromUrl || brandFromUrl || searchFromUrl)}
            />

            {(minPrice || maxPrice) && (
                <div className="active-filters-row">
                    {minPrice && (
                        <div className="active-filter-chip">
                            От {minPrice} TJS
                            <button onClick={() => setMinPrice('')}>✕</button>
                        </div>
                    )}
                    {maxPrice && (
                        <div className="active-filter-chip">
                            До {maxPrice} TJS
                            <button onClick={() => setMaxPrice('')}>✕</button>
                        </div>
                    )}
                    <button onClick={resetFilters} className="clear-all-filters">
                        Очистить всё
                    </button>
                </div>
            )}

            {showFilters && (
                <div className="filter-sheet">
                    <h3 className="filter-sheet-title">Фильтр по цене</h3>
                    <div className="filter-sheet-group">
                        <input
                            type="number"
                            placeholder="От TJS"
                            value={minPrice}
                            onChange={e => setMinPrice(e.target.value)}
                            className="filter-sheet-input"
                        />
                        <span className="filter-sheet-separator">—</span>
                        <input
                            type="number"
                            placeholder="До TJS"
                            value={maxPrice}
                            onChange={e => setMaxPrice(e.target.value)}
                            className="filter-sheet-input"
                        />
                    </div>
                </div>
            )}

            {loading ? (
                <div className="loader-container"><div className="spinner" /></div>
            ) : products.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-state-icon">🔍</div>
                    <h3>Товары не найдены</h3>
                    <p>Попробуйте изменить параметры поиска</p>
                    {hasActiveFilters && (
                        <button onClick={resetFilters} className="auth-button">
                            Сбросить фильтры
                        </button>
                    )}
                </div>
            ) : (
                <>
                    <div className="products-list">
                        {products.map(product => (
                            <ProductCard key={product.id} product={product} />
                        ))}
                    </div>
                    <div ref={sentinelRef} style={{ height: 1 }} />
                    {loadingMore && (
                        <div className="loader-more">
                            <div className="spinner-small" />
                            <span>Загрузка...</span>
                        </div>
                    )}
                    {!hasMore && !loadingMore && (
                        <div className="end-message">
                            {products.length >= 200 ? (
                                <>
                                    <p>Показано {products.length} из {total} товаров</p>
                                    <p className="hint">Уточните фильтры, чтобы найти нужное</p>
                                </>
                            ) : (
                                <p>Показано все {products.length} товаров</p>
                            )}
                        </div>
                    )}
                </>
            )}

            {showScrollButton && (
                <button
                    className="scroll-to-top-btn"
                    onClick={scrollToTop}
                    aria-label="Наверх"
                >
                    ↑
                </button>
            )}
        </div>
    );
};
